import { Search } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRoutes } from '@/hooks/useRoutes';
import { useStops } from '@/hooks/useStops';
import { cn } from '@/lib/utils';

interface GlobalSearchProps {
  className?: string;
}

export function GlobalSearch({ className }: GlobalSearchProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const debounced = useDebouncedValue(query, 150).toLowerCase();
  const navigate = useNavigate();

  const routesQuery = useRoutes();
  // Routes are a small fixed set (~22) so client-side filtering is fine.
  // Stops number in the thousands; ask the backend instead — the prior
  // implementation filtered against the first 50 stops sorted by name,
  // so anything past "Boylston" was unreachable.
  const stopsQuery = useStops(debounced ? { q: debounced, limit: 20 } : { limit: 6 });

  const matchingRoutes = React.useMemo(() => {
    const data = routesQuery.data?.data ?? [];
    if (!debounced) return data.slice(0, 6);
    return data
      .filter(
        (r) =>
          r.long_name.toLowerCase().includes(debounced) ||
          r.id.toLowerCase().includes(debounced) ||
          (r.short_name?.toLowerCase().includes(debounced) ?? false),
      )
      .slice(0, 6);
  }, [routesQuery.data, debounced]);

  const matchingStops = stopsQuery.data?.data ?? [];

  const go = React.useCallback(
    (path: string) => {
      setOpen(false);
      setQuery('');
      navigate(path);
    },
    [navigate],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'w-10 justify-center px-0 text-muted-foreground sm:w-full sm:max-w-md sm:justify-start sm:px-4',
            className,
          )}
          aria-label="Open global search"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          <span className="hidden truncate sm:inline">Search routes, stops…</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(28rem,calc(100vw-2rem))] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search routes and stops"
            value={query}
            onValueChange={setQuery}
            aria-label="Global search input"
          />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            {matchingRoutes.length > 0 ? (
              <CommandGroup heading="Routes">
                {matchingRoutes.map((route) => (
                  <CommandItem
                    key={route.id}
                    value={`route-${route.id}`}
                    onSelect={() => {
                      go(`/routes/${route.id}`);
                    }}
                  >
                    <span className="font-medium">{route.long_name}</span>
                    <span className="ml-auto text-muted-foreground">{route.id}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {matchingStops.length > 0 ? (
              <CommandGroup heading="Stops">
                {matchingStops.map((stop) => (
                  <CommandItem
                    key={stop.id}
                    value={`stop-${stop.id}`}
                    onSelect={() => {
                      go(`/stops/${stop.id}`);
                    }}
                  >
                    <span className="font-medium">{stop.name}</span>
                    <span className="ml-auto text-muted-foreground">{stop.id}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
